import { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useTranslation } from "react-i18next";
import { getDifficultyIcon, getDifficultyName, getElevationSpan, getMaxElevation, getMinElevation, getStatusColor, getStatusText, toKilometerString } from "../../../utils";
import { useThemedStyleFunction } from "../../theme-provider";
import { ThemedText } from '../../themed-text';
import { Path, PathCategory, PathGpsCoordinate } from '../../../interfaces';
import { getTranslatedProperty } from '../../../utils/localization.utils';
import { detailsContentStyle } from '../details-content.style';
import { SmallArrowDown } from '../../../assets';

type CompositeChildOption = {
    id: string,
    name: string,
};

type PathDetailsContentTopProps = Path & {
    gpsCoordinates: PathGpsCoordinate[],
    category: PathCategory,
    compositeChildren?: CompositeChildOption[],
    onCompositeChildPress?: (childId: string) => void,
    compositeParentName?: string,
    onCompositeParentPress?: () => void,
};

const PathDetailsContentTop = (props: PathDetailsContentTopProps) => {
    const {gpsCoordinates, category, difficulty, length, status, groomedAt, tags, compositeChildren, onCompositeChildPress, compositeParentName, onCompositeParentPress} = props;
    const [dropdownExpanded, setDropdownExpanded] = useState(false);

    const [t] = useTranslation();
    
    const DifficultyIcon = (props: any) => getDifficultyIcon(difficulty, props);
    const difficultyName = getDifficultyName(t, difficulty, false);
    
    const formattedLength = toKilometerString(length);
    
    const statusText = getStatusText(t, status, groomedAt);
    const statusColor = getStatusColor(status, groomedAt);

    const elevation = getElevationSpan(getMinElevation(gpsCoordinates), getMaxElevation(gpsCoordinates));
    const formattedElevation = `${elevation} m`;

    const formattedTags = tags.map((tag => getTranslatedProperty('name', tag)?.toLowerCase()));
    const metadata = [difficultyName, ...formattedTags].join(', ');

    const categoryName = getTranslatedProperty('name', category);
    const hasCompositeChildren = (compositeChildren?.length ?? 0) > 0;

    const style = useThemedStyleFunction(detailsContentStyle);

    return (
        <View style={style.container}>
            {hasCompositeChildren && (
                <View style={style.compositeDropdownWrapper}>
                    <TouchableOpacity
                        style={style.compositeDropdownHeaderRow}
                        onPress={() => setDropdownExpanded((prev) => !prev)}
                        hitSlop={8}
                        activeOpacity={0.8}
                        accessibilityRole="button"
                        accessibilityState={{ expanded: dropdownExpanded }}
                    >
                        <View style={style.compositeDropdownToggle}>
                            <View style={dropdownExpanded ? style.expandIconExpanded : style.expandIcon}>
                                <SmallArrowDown
                                    color={style.expandButtonIcon.color as string}
                                    width={12}
                                    height={12}
                                />
                            </View>
                        </View>
                        <ThemedText style={style.compositeDropdownLabel}>
                            {t('compositeChildrenDropdownLabel', { defaultValue: 'Subpaths' })}
                        </ThemedText>
                    </TouchableOpacity>

                    {dropdownExpanded && (
                        <View style={style.compositeDropdownList}>
                            {(compositeChildren ?? []).map((child, index) => (
                                <TouchableOpacity
                                    key={child.id}
                                    onPress={() => {
                                        setDropdownExpanded(false);
                                        onCompositeChildPress?.(child.id);
                                    }}
                                    style={[
                                        style.compositeDropdownItem,
                                        index === (compositeChildren?.length ?? 0) - 1 ? style.compositeDropdownItemLast : undefined,
                                    ]}
                                >
                                    <ThemedText style={style.compositeDropdownItemText}>
                                        {child.name}
                                    </ThemedText>
                                </TouchableOpacity>
                            ))}
                        </View>
                    )}
                </View>
            )}

            {onCompositeParentPress && compositeParentName && (
                <View style={style.compositeParentWrapper}>
                    <View style={style.compositeParentConnector} />
                    <TouchableOpacity onPress={onCompositeParentPress} accessibilityRole="link">
                        <ThemedText style={style.compositeParentText}>
                            {t('compositeParentLink', { name: compositeParentName, defaultValue: 'Go to composite trail: {{name}}' })}
                        </ThemedText>
                    </TouchableOpacity>
                </View>
            )}

            <View style={style.row}>
                <DifficultyIcon style={style.difficultyIcon}/>
                <ThemedText style={[style.standard, style.secondary]}>{metadata}</ThemedText>
            </View>
            <View style={style.paragraph}>
                <View style={style.row}>
                    <ThemedText style={[style.standard, style.primary]} type='bold'>{formattedLength}</ThemedText>
                    <ThemedText style={[style.standard, style.primary]}> {categoryName}, </ThemedText>
                    <ThemedText style={[style.standard, style.primary]} type='bold'>{formattedElevation}</ThemedText>
                    <ThemedText style={[style.standard, style.primary]}> altitude </ThemedText>
                </View>
                <ThemedText style={[style.standard, style.capitalize, {color: statusColor}]} type='bold'>{statusText}</ThemedText>
            </View>
        </View>
    )
};

export { PathDetailsContentTop };